import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Search, GraduationCap, MapPin, Calendar, Star, TrendingUp, Users, Filter, ArrowLeft, Loader2, AlertCircle } from 'lucide-react';
import { Disclosure } from '@headlessui/react';
import { ChevronUpIcon, ChevronDownIcon, AdjustmentsHorizontalIcon, Squares2X2Icon, TableCellsIcon } from '@heroicons/react/24/outline';
import InputForm from './InputForm';
import apiService from '../services/api';

// Loading Component
const DataLoader = ({ loading, error, onRetry }) => (
  <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
    <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
      {loading ? (
        <>
          <div className="flex justify-center mb-4">
            <Loader2 className="h-12 w-12 text-blue-600 animate-spin" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Loading NEET PG Data</h2>
          <p className="text-gray-600 mb-4">
            Fetching the latest college, course, and allotment data...
          </p>
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-sm text-blue-700">
              This may take a few moments. The data is being loaded from our secure servers.
            </p>
          </div>
        </>
      ) : error ? (
        <>
          <div className="flex justify-center mb-4">
            <AlertCircle className="h-12 w-12 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Failed to Load Data</h2>
          <p className="text-gray-600 mb-4">
            {error}
          </p>
          <div className="space-y-3">
            <button
              onClick={onRetry}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-xl hover:bg-blue-700 transition-colors font-semibold"
            >
              Retry Loading
            </button>
            <div className="bg-yellow-50 rounded-lg p-4">
              <p className="text-sm text-yellow-700">
                Make sure you're connected to the internet and the backend server is running.
              </p>
            </div>
          </div>
        </>
      ) : null}
    </div>
  </div>
);

const PredictionCard = ({ prediction, userRank }) => {
  const [showHistorical, setShowHistorical] = useState(false);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Bond display logic
  const bondYears = Number(prediction.fee.bond) || 0;
  const bondDisplay = bondYears === 0 ? 'No Bond' : `${bondYears} year${bondYears > 1 ? 's' : ''}`;

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow duration-200">
      <div className="p-6">
        <div className="space-y-1 mb-4">
          {/* Row 1: College Name + Chance */}
          <div className="flex items-center justify-between">
            <h4 className="text-xl font-bold text-gray-900">
              {prediction.college.college_name}
            </h4>
            <div className={`inline-flex items-center px-4 py-1 rounded-full text-sm font-semibold ${prediction.chanceColor === 'green' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
              {prediction.chanceText}
            </div>
          </div>
          
          {/* Row 2: Course Name + Ranks ahead/behind */}
          <div className="flex items-center justify-between">
            <h5 className="text-lg font-semibold text-blue-600">
              {prediction.course.course_name}
            </h5>
            <div className={`text-xs ${prediction.chanceColor === 'green' ? 'text-green-600' : 'text-yellow-600'}`}>
              {parseInt(userRank) <= prediction.closingRank ? `${prediction.closingRank - parseInt(userRank)} ranks ahead` : `${parseInt(userRank) - prediction.closingRank} ranks behind`}
            </div>
          </div>
          
          {/* Row 3: Hospital Details + Rank Comparison */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <div className="flex items-center">
                <MapPin className="h-4 w-4 mr-1" />
                {prediction.college.state}
              </div>
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-1" />
                Est. {prediction.college.established_year}
              </div>
              <div className="flex items-center">
                <Users className="h-4 w-4 mr-1" />
                {prediction.college.no_of_beds} beds
              </div>
              <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {prediction.college.management_type}
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center space-x-2">
                <div className="text-center">
                  <div className="text-sm font-bold text-blue-600">{userRank}</div>
                  <div className="text-xs text-gray-500">Your Rank</div>
                </div>
                <div className="text-xs text-gray-400">vs</div>
                <div className="text-center">
                  <div className="text-sm font-bold text-gray-700">{prediction.closingRank}</div>
                  <div className="text-xs text-gray-500">{prediction.year} {prediction.round && prediction.round.toString().toLowerCase() !== 'null' ? prediction.round : ''}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 4: Financial Details Grid */}
        <div className="grid grid-cols-4 gap-4 text-center mb-4">
          <div>
            <div className="text-lg font-bold text-gray-900">
              {formatCurrency(prediction.fee.course_fee)}
            </div>
            <div className="text-xs text-gray-600 font-medium">Fees</div>
          </div>
          
          <div>
            <div className="text-lg font-bold text-gray-900">
              {formatCurrency(prediction.fee.stipend_y1)}
            </div>
            <div className="text-xs text-gray-600 font-medium">Stipend</div>
          </div>
          
          <div>
            <div className={`text-lg font-bold ${bondYears > 0 ? 'text-red-900' : 'text-green-900'}`}>{bondDisplay}</div>
            <div className="text-xs text-gray-600 font-medium">Bond</div>
          </div>
          
          <div>
            <div className={`text-lg font-bold ${bondYears > 0 && prediction.fee.bond_penalty > 0 ? 'text-red-900' : 'text-gray-900'}`}>{bondYears > 0 && prediction.fee.bond_penalty > 0 ? formatCurrency(prediction.fee.bond_penalty) : 'N/A'}</div>
            <div className="text-xs text-gray-600 font-medium">Bond Penalty</div>
          </div>
        </div>

        {prediction.historicalData && prediction.historicalData.length > 0 && (
          <div className="border-t border-gray-200 pt-4">
            <button
              onClick={() => setShowHistorical(!showHistorical)}
              className="flex items-center justify-between w-full text-left"
            >
              <div className="flex items-center space-x-3">
                <h6 className="font-semibold text-gray-900 flex items-center text-sm">
                  <TrendingUp className="h-4 w-4 mr-1" />
                  Historical Closing Ranks ({prediction.historicalData.length} records)
                </h6>
                {/* Allotment Details in accordion heading */}
                <div className="flex items-center space-x-2">
                  <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {prediction.quota}
                  </div>
                  <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    {prediction.allotted_category}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {/* Trend in accordion heading - moved to right side */}
                {prediction.historicalData.length > 1 && (() => {
                  // Get unique years and find lowest rank (highest number) for each year
                  const yearlyLowestRanks = {};
                  prediction.historicalData.forEach(d => {
                    if (!yearlyLowestRanks[d.year] || d.rank > yearlyLowestRanks[d.year]) {
                      yearlyLowestRanks[d.year] = d.rank;
                    }
                  });
                  const years = Object.keys(yearlyLowestRanks).map(Number).sort((a, b) => b - a);
                  if (years.length >= 2) {
                    const latestYearRank = yearlyLowestRanks[years[0]];
                    const previousYearRank = yearlyLowestRanks[years[1]];
                    const rankDifference = latestYearRank - previousYearRank;
                    const isGettingEasier = rankDifference > 0;
                    const trendPercentage = Math.abs((rankDifference / previousYearRank) * 100).toFixed(1);
                    return (
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        isGettingEasier ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {isGettingEasier ? '↘' : '↗'} {trendPercentage}% {isGettingEasier ? 'Easier' : 'Harder'}
                      </div>
                    );
                  }
                  return null;
                })()}
                {showHistorical ? (
                  <ChevronUpIcon className="h-6 w-6 text-gray-700 font-bold" />
                ) : (
                  <ChevronDownIcon className="h-6 w-6 text-gray-700 font-bold" />
                )}
              </div>
            </button>
            {showHistorical && (
              <div className="mt-3 bg-gray-50 rounded-lg p-4">
                <div className="overflow-x-auto">
                  {/* Build a map: { year: { round: { rank, count } } } */}
                  {(() => {
                    const grouped = {};
                    prediction.historicalData.forEach(d => {
                      const year = d.year;
                      const round = d.round && d.round !== null && d.round !== undefined && d.round.toString().toLowerCase() !== 'null' ? 
                        d.round : 
                        'No Round Data';
                      if (!grouped[year]) grouped[year] = {};
                      if (!grouped[year][round]) grouped[year][round] = { rank: d.rank, count: 1 };
                      else {
                        // Update to lowest (highest number) rank and increment count
                        if (d.rank > grouped[year][round].rank) grouped[year][round].rank = d.rank;
                        grouped[year][round].count += 1;
                      }
                    });
                    const years = Object.keys(grouped).sort((a, b) => b - a);
                    // Collect all unique rounds across all years
                    const allRounds = Array.from(new Set(years.flatMap(y => Object.keys(grouped[y])))).sort((a, b) => {
                      // Sort R1, R2, ... numerically, but put No Round Data at the end
                      if (a === 'No Round Data') return 1;
                      if (b === 'No Round Data') return -1;
                      const numA = parseInt(a.replace(/[^\d]/g, ''));
                      const numB = parseInt(b.replace(/[^\d]/g, ''));
                      return numA - numB;
                    });
                    return (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-2 text-xs font-semibold text-gray-600">Year</th>
                            {allRounds.map(round => (
                              <th key={round} className="text-center py-2 text-xs font-semibold text-gray-600">{round}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {years.map(year => (
                            <tr key={year} className="border-b border-gray-100">
                              <td className="py-2 font-medium">{year}</td>
                              {allRounds.map(round => (
                                <td key={round} className="py-2 text-center">
                                  {grouped[year][round] ? (
                                    <span>
                                      {grouped[year][round].rank}
                                      <span className="text-xs text-gray-500 ml-1">({grouped[year][round].count})</span>
                                    </span>
                                  ) : (
                                    <span className="text-gray-300">-</span>
                                  )}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Table view component for predictions
const PredictionsTable = ({ predictions, userRank, formatCurrency, asTableRows }) => {
  // Removed unused variables to fix ESLint warnings

  if (asTableRows) {
    return (
      <>
        <thead className="bg-gray-50">
          <tr>
            <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">State</th>
            <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-80">College</th>
            <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Type</th>
            <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Course</th>
            <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">Fees</th>
            <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">Stipend</th>
            <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Closing Ranks</th>
            <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">Beds</th>
            <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">Chance</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {predictions.map((prediction, index) => {
            return (
              <tr key={index} className="hover:bg-gray-50">
                <td className="px-2 py-2">
                  <div className="text-xs font-medium text-gray-900">{prediction.college.state}</div>
                </td>
                <td className="px-2 py-2">
                  <div className="text-xs font-medium text-gray-900 leading-tight">{prediction.college.college_name}</div>
                </td>
                <td className="px-2 py-2">
                  <span className="inline-flex items-center px-1 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                    {prediction.college.management_type}
                  </span>
                </td>
                <td className="px-2 py-2">
                  <div className="text-xs font-medium text-blue-600 leading-tight">{prediction.course.course_name}</div>
                </td>
                <td className="px-2 py-2">
                  <div className="text-xs font-bold text-gray-900">{formatCurrency(prediction.fee.course_fee)}</div>
                </td>
                <td className="px-2 py-2">
                  <div className="text-xs font-bold text-gray-900">{formatCurrency(prediction.fee.stipend_y1)}</div>
                </td>
                <td className="px-2 py-2">
                  <div className="text-center">
                    <div className="text-xs font-bold text-gray-700">{prediction.closingRank}</div>
                    <div className="text-xs text-gray-500">{prediction.year} {prediction.round && prediction.round.toString().toLowerCase() !== 'null' ? prediction.round : ''}</div>
                  </div>
                </td>
                <td className="px-2 py-2">
                  <div className="text-xs font-medium text-gray-900">{prediction.college.no_of_beds}</div>
                </td>
                <td className="px-2 py-2">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${prediction.chanceColor === 'green' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                    {prediction.chanceText}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </>
    );
  }
  
  return null;
};

// --- Move InputPage and ResultsPage to top-level ---
const InputPage = ({ userInputs, handleInputChange, handlePredict, loading, filterOptions, maxFee, maxStipend }) => (
  <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-12 px-4">
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-12">
        <h1 className="text-5xl font-bold text-gray-900 mb-6">
          NEET PG College Predictor
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Get personalized predictions for your NEET PG college admissions based on your rank, 
          category, and preferences. Make informed decisions about your medical career.
        </p>
      </div>
      <InputForm 
        userInputs={userInputs}
        handleInputChange={handleInputChange}
        handlePredict={handlePredict}
        loading={loading}
        filterOptions={filterOptions}
        maxFee={maxFee}
        maxStipend={maxStipend}
      />
    </div>
  </div>
);

const ResultsPage = ({
  userInputs,
  predictions,
  filteredPredictions,
  filters,
  filterOptions,
  formatCurrency,
  resetFilters,
  updateFilter,
  loading,
  setCurrentPage,
  maxFee,
  maxStipend,
  viewMode,
  setViewMode,
  currentPage,
  rowsPerPage,
  paginatedData,
  totalPages,
  setCurrentPageNum,
  cardCurrentPage,
  setCardCurrentPage,
  cardTotalPages,
  paginatedCardData,
  cardRowsPerPage,
  filteringLoading,
  searchTerms,
  setSearchTerms
}) => {

  // Removed unused variables to fix ESLint warnings

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="bg-white shadow-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-3 rounded-xl">
                <GraduationCap className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Your Predictions
                </h1>
                <p className="text-gray-600">Based on your profile and preferences</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 bg-yellow-50 px-4 py-2 rounded-full border border-yellow-200">
              <Star className="h-4 w-4 text-yellow-500" />
              <span className="text-sm font-medium text-yellow-700">Free Version</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-8 border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900">Your Profile Summary</h3>
            <button
              onClick={() => setCurrentPage('input')}
              className="flex items-center space-x-2 text-white bg-blue-600 hover:bg-blue-700 font-semibold px-5 py-2 rounded-xl shadow transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <ArrowLeft className="h-5 w-5" />
              <span>Modify Search</span>
            </button>
          </div>
          <div className="grid md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-xl">
              <div className="text-sm text-blue-600 font-medium">NEET PG Rank</div>
              <div className="text-2xl font-bold text-blue-900">{userInputs.rank}</div>
            </div>
            <div className="bg-green-50 p-4 rounded-xl">
              <div className="text-sm text-green-600 font-medium">Quota</div>
              <div className="text-lg font-bold text-green-900">{userInputs.quota}</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-xl">
              <div className="text-sm text-purple-600 font-medium">Category</div>
              <div className="text-lg font-bold text-purple-900">{userInputs.category}</div>
            </div>
            <div className="bg-orange-50 p-4 rounded-xl">
              <div className="text-sm text-orange-600 font-medium">Budget</div>
              <div className="text-lg font-bold text-orange-900">
                {userInputs.budget ? formatCurrency(parseInt(userInputs.budget)) : 'No Limit'}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content with Left Sidebar */}
        <div className="w-full max-w-full grid grid-cols-10 gap-10 bg-gray-50">
          {/* Left Sidebar - Filters */}
          <div className="col-span-2 min-w-[240px]">
            <div className="bg-white rounded-2xl shadow-xl p-2 border border-gray-100 sticky top-2 max-h-[96vh] overflow-y-auto text-sm" style={{ minWidth: 240 }}>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-2">
                  <AdjustmentsHorizontalIcon className="h-6 w-6 text-blue-500" />
                  <h3 className="text-lg font-bold text-gray-900">Filters</h3>
                  {/* Active filter count badge */}
                  {(() => {
                    const count = [
                      filters.colleges.length,
                      filters.courses.length,
                      filters.states.length,
                      filters.ownershipTypes.length,
                      filters.clinicalTypes.length,
                      filters.bondRequired !== 'all' ? 1 : 0,
                      filters.chanceLevel !== 'all' ? 1 : 0
                    ].reduce((a, b) => a + b, 0);
                    return count > 0 ? (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">{count}</span>
                    ) : null;
                  })()}
                </div>
                <button
                  onClick={resetFilters}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  Reset All
                </button>
              </div>
              <div className="space-y-4">
                {/* Chance Level Filter (Accordion) */}
                <Disclosure>
                  {({ open }) => (
                    <div>
                      <Disclosure.Button className="flex w-full justify-between items-center py-2 text-left">
                        <span className="block text-sm font-semibold text-gray-700">Chance Level</span>
                        {open ? <ChevronUpIcon className="h-5 w-5 text-blue-500" /> : <ChevronDownIcon className="h-5 w-5 text-gray-400" />}
                      </Disclosure.Button>
                      <Disclosure.Panel>
                        <select
                          value={filters.chanceLevel}
                          onChange={(e) => updateFilter('chanceLevel', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                        >
                          <option value="all">All Chances</option>
                          <option value="high">High Chance Only</option>
                          <option value="moderate">Moderate Chance Only</option>
                        </select>
                      </Disclosure.Panel>
                    </div>
                  )}
                </Disclosure>

                {/* Colleges Filter (Accordion) */}
                <Disclosure>
                  {({ open }) => (
                    <div>
                      <Disclosure.Button className="flex w-full justify-between items-center py-2 text-left">
                        <span className="block text-sm font-semibold text-gray-700">Colleges</span>
                        {open ? <ChevronUpIcon className="h-5 w-5 text-blue-500" /> : <ChevronDownIcon className="h-5 w-5 text-gray-400" />}
                      </Disclosure.Button>
                      <Disclosure.Panel>
                        <input
                          type="text"
                          placeholder="Search colleges..."
                          className="w-full mb-2 px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-sm"
                          value={searchTerms.collegesSearch || ''}
                          onChange={e => setSearchTerms(prev => ({ ...prev, collegesSearch: e.target.value }))}
                        />
                        <div className="border border-gray-300 rounded-lg p-3 max-h-32 overflow-y-auto bg-gray-50">
                          {filterOptions.colleges?.filter(college =>
                            !searchTerms.collegesSearch || (college && typeof college === 'string' && 
                              college.toLowerCase().split(' ').some(word => 
                                word.startsWith((searchTerms.collegesSearch || '').toLowerCase())
                              ))
                          ).map(college => (
                            <label key={college} className="flex items-center space-x-2 cursor-pointer hover:bg-white p-1 rounded transition-colors">
                              <input
                                type="checkbox"
                                checked={filters.colleges.includes(college)}
                                onChange={() => updateFilter('colleges', college)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                              />
                              <span className="text-sm text-gray-700">{college}</span>
                            </label>
                          ))}
                        </div>
                      </Disclosure.Panel>
                    </div>
                  )}
                </Disclosure>

                {/* Courses Filter (Accordion) */}
                <Disclosure>
                  {({ open }) => (
                    <div>
                      <Disclosure.Button className="flex w-full justify-between items-center py-2 text-left">
                        <span className="block text-sm font-semibold text-gray-700">Courses</span>
                        {open ? <ChevronUpIcon className="h-5 w-5 text-blue-500" /> : <ChevronDownIcon className="h-5 w-5 text-gray-400" />}
                      </Disclosure.Button>
                      <Disclosure.Panel>
                        <input
                          type="text"
                          placeholder="Search courses..."
                          className="w-full mb-2 px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-sm"
                          value={searchTerms.coursesSearch || ''}
                          onChange={e => setSearchTerms(prev => ({ ...prev, coursesSearch: e.target.value }))}
                        />
                        <div className="border border-gray-300 rounded-lg p-3 max-h-32 overflow-y-auto bg-gray-50">
                          {filterOptions.courses?.filter(course =>
                            !searchTerms.coursesSearch || (course && typeof course === 'string' && 
                              course.toLowerCase().split(' ').some(word => 
                                word.startsWith((searchTerms.coursesSearch || '').toLowerCase())
                              ))
                          ).map(course => (
                            <label key={course} className="flex items-center space-x-2 cursor-pointer hover:bg-white p-1 rounded transition-colors">
                              <input
                                type="checkbox"
                                checked={filters.courses.includes(course)}
                                onChange={() => updateFilter('courses', course)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                              />
                              <span className="text-sm text-gray-700">{course}</span>
                            </label>
                          ))}
                        </div>
                      </Disclosure.Panel>
                    </div>
                  )}
                </Disclosure>

                {/* States Filter (Accordion) */}
                <Disclosure>
                  {({ open }) => (
                    <div>
                      <Disclosure.Button className="flex w-full justify-between items-center py-2 text-left">
                        <span className="block text-sm font-semibold text-gray-700">States</span>
                        {open ? <ChevronUpIcon className="h-5 w-5 text-blue-500" /> : <ChevronDownIcon className="h-5 w-5 text-gray-400" />}
                      </Disclosure.Button>
                      <Disclosure.Panel>
                        <input
                          type="text"
                          placeholder="Search states..."
                          className="w-full mb-2 px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-sm"
                          value={searchTerms.statesSearch || ''}
                          onChange={e => setSearchTerms(prev => ({ ...prev, statesSearch: e.target.value }))}
                        />
                        <div className="border border-gray-300 rounded-lg p-3 max-h-32 overflow-y-auto bg-gray-50">
                          {filterOptions.states?.filter(state =>
                            !searchTerms.statesSearch || (state && typeof state === 'string' && 
                              state.toLowerCase().split(' ').some(word => 
                                word.startsWith((searchTerms.statesSearch || '').toLowerCase())
                              ))
                          ).map(state => (
                            <label key={state} className="flex items-center space-x-2 cursor-pointer hover:bg-white p-1 rounded transition-colors">
                              <input
                                type="checkbox"
                                checked={filters.states.includes(state)}
                                onChange={() => updateFilter('states', state)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                              />
                              <span className="text-sm text-gray-700">{state}</span>
                            </label>
                          ))}
                        </div>
                      </Disclosure.Panel>
                    </div>
                  )}
                </Disclosure>

                {/* Ownership Type Filter (Accordion) */}
                <Disclosure>
                  {({ open }) => (
                    <div>
                      <Disclosure.Button className="flex w-full justify-between items-center py-2 text-left">
                        <span className="block text-sm font-semibold text-gray-700">Ownership Type</span>
                        {open ? <ChevronUpIcon className="h-5 w-5 text-blue-500" /> : <ChevronDownIcon className="h-5 w-5 text-gray-400" />}
                      </Disclosure.Button>
                      <Disclosure.Panel>
                        <input
                          type="text"
                          placeholder="Search ownership types..."
                          className="w-full mb-2 px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-sm"
                          value={searchTerms.ownershipTypesSearch || ''}
                          onChange={e => setSearchTerms(prev => ({ ...prev, ownershipTypesSearch: e.target.value }))}
                        />
                        <div className="border border-gray-300 rounded-lg p-3 max-h-32 overflow-y-auto bg-gray-50">
                          {filterOptions.ownershipTypes?.filter(type =>
                            !searchTerms.ownershipTypesSearch || (type && typeof type === 'string' && 
                              type.toLowerCase().split(' ').some(word => 
                                word.startsWith((searchTerms.ownershipTypesSearch || '').toLowerCase())
                              ))
                          ).map(type => (
                            <label key={type} className="flex items-center space-x-2 cursor-pointer hover:bg-white p-1 rounded transition-colors">
                              <input
                                type="checkbox"
                                checked={filters.ownershipTypes.includes(type)}
                                onChange={() => updateFilter('ownershipTypes', type)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                              />
                              <span className="text-sm text-gray-700">{type}</span>
                            </label>
                          ))}
                        </div>
                      </Disclosure.Panel>
                    </div>
                  )}
                </Disclosure>

                {/* Clinical Type Filter (Accordion) */}
                <Disclosure>
                  {({ open }) => (
                    <div>
                      <Disclosure.Button className="flex w-full justify-between items-center py-2 text-left">
                        <span className="block text-sm font-semibold text-gray-700">Clinical Type</span>
                        {open ? <ChevronUpIcon className="h-5 w-5 text-blue-500" /> : <ChevronDownIcon className="h-5 w-5 text-gray-400" />}
                      </Disclosure.Button>
                      <Disclosure.Panel>
                        <input
                          type="text"
                          placeholder="Search clinical types..."
                          className="w-full mb-2 px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-sm"
                          value={searchTerms.clinicalTypesSearch || ''}
                          onChange={e => setSearchTerms(prev => ({ ...prev, clinicalTypesSearch: e.target.value }))}
                        />
                        <div className="border border-gray-300 rounded-lg p-3 max-h-32 overflow-y-auto bg-gray-50">
                          {filterOptions.clinicalTypes?.filter(type =>
                            !searchTerms.clinicalTypesSearch || (type && typeof type === 'string' && 
                              type.toLowerCase().split(' ').some(word => 
                                word.startsWith((searchTerms.clinicalTypesSearch || '').toLowerCase())
                              ))
                          ).map(type => (
                            <label key={type} className="flex items-center space-x-2 cursor-pointer hover:bg-white p-1 rounded transition-colors">
                              <input
                                type="checkbox"
                                checked={filters.clinicalTypes.includes(type)}
                                onChange={() => updateFilter('clinicalTypes', type)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                              />
                              <span className="text-sm text-gray-700">{type}</span>
                            </label>
                          ))}
                        </div>
                      </Disclosure.Panel>
                    </div>
                  )}
                </Disclosure>

                {/* Fee Range Filter (not accordion) */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Fee Range
                  </label>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        placeholder="Min"
                        value={filters.feeRange.min}
                        onChange={(e) => updateFilter('feeRange', { ...filters.feeRange, min: parseInt(e.target.value) || 0 })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                      />
                      <span className="text-gray-500">to</span>
                      <input
                        type="number"
                        placeholder="Max"
                        value={filters.feeRange.max}
                        onChange={(e) => updateFilter('feeRange', { ...filters.feeRange, max: parseInt(e.target.value) || maxFee })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                      />
                    </div>
                    <div className="px-2">
                      <input
                        type="range"
                        min="0"
                        max={maxFee}
                        step="10000"
                        value={filters.feeRange.max}
                        onChange={(e) => updateFilter('feeRange', { ...filters.feeRange, max: parseInt(e.target.value) })}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                      />
                    </div>
                    <div className="text-xs text-gray-500 text-center">
                      {formatCurrency(filters.feeRange.min)} - {formatCurrency(filters.feeRange.max)}
                    </div>
                  </div>
                </div>

                {/* Stipend Range Filter (not accordion) */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Stipend (Year 1)
                  </label>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        placeholder="Min"
                        value={filters.stipendRange.min}
                        onChange={(e) => updateFilter('stipendRange', { ...filters.stipendRange, min: parseInt(e.target.value) || 0 })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                      />
                      <span className="text-gray-500">to</span>
                      <input
                        type="number"
                        placeholder="Max"
                        value={filters.stipendRange.max}
                        onChange={(e) => updateFilter('stipendRange', { ...filters.stipendRange, max: parseInt(e.target.value) || maxStipend })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                      />
                    </div>
                    <div className="px-2">
                      <input
                        type="range"
                        min="0"
                        max={maxStipend}
                        step="1000"
                        value={filters.stipendRange.max}
                        onChange={(e) => updateFilter('stipendRange', { ...filters.stipendRange, max: parseInt(e.target.value) })}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                      />
                    </div>
                    <div className="text-xs text-gray-500 text-center">
                      {formatCurrency(filters.stipendRange.min)} - {formatCurrency(filters.stipendRange.max)}
                    </div>
                  </div>
                </div>

                {/* Bond Requirement Filter (Accordion) */}
                <Disclosure>
                  {({ open }) => (
                    <div>
                      <Disclosure.Button className="flex w-full justify-between items-center py-2 text-left">
                        <span className="block text-sm font-semibold text-gray-700">Bond Requirement</span>
                        {open ? <ChevronUpIcon className="h-5 w-5 text-blue-500" /> : <ChevronDownIcon className="h-5 w-5 text-gray-400" />}
                      </Disclosure.Button>
                      <Disclosure.Panel>
                        <select
                          value={filters.bondRequired}
                          onChange={(e) => updateFilter('bondRequired', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                        >
                          <option value="all">Any Bond Status</option>
                          <option value="no">No Bond Required</option>
                          <option value="yes">Bond Required</option>
                        </select>
                      </Disclosure.Panel>
                    </div>
                  )}
                </Disclosure>
              </div>

              {/* Results Count */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="text-center">
                  <div className="text-sm text-gray-600">Showing</div>
                  <div className="text-2xl font-bold text-blue-600">{filteredPredictions.length}</div>
                  <div className="text-sm text-gray-600">of {predictions.length} results</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Content - Results */}
          <div className="col-span-8">
            {filteredPredictions.length > 0 && (
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 w-full max-w-full flex flex-col max-h-[96vh] min-h-[600px] overflow-hidden relative">
                {/* Header: Title, match count, toggle */}
                <div className="flex items-center justify-between px-6 pt-6 pb-2 border-b border-gray-100">
                  <h3 className="text-2xl font-bold text-gray-900">Your College Predictions</h3>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2 bg-green-50 px-4 py-2 rounded-full">
                      <div className="h-2 w-2 bg-green-500 rounded-full"></div>
                      <span className="text-sm font-medium text-green-700">
                        {filteredPredictions.length} matches found
                      </span>
                    </div>
                    {/* View mode toggle */}
                    <div className="flex items-center bg-gray-100 rounded-lg p-1">
                      <button
                        onClick={() => setViewMode('cards')}
                        className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          viewMode === 'cards' 
                            ? 'bg-white text-blue-600 shadow-sm' 
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        <Squares2X2Icon className="h-4 w-4" />
                        <span>Cards</span>
                      </button>
                      <button
                        onClick={() => setViewMode('table')}
                        className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          viewMode === 'table' 
                            ? 'bg-white text-blue-600 shadow-sm' 
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        <TableCellsIcon className="h-4 w-4" />
                        <span>Table</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Scrollable content area for both cards and table */}
                <div className="flex-1 w-full max-w-full overflow-y-auto relative" style={{ scrollbarWidth: 'auto', scrollbarColor: '#cbd5e0 #f7fafc' }}>
                  {filteringLoading && (
                    <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                      <div className="flex flex-col items-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
                        <span className="text-sm text-gray-600">Applying filters...</span>
                      </div>
                    </div>
                  )}
                  {viewMode === 'cards' ? (
                    <div className="grid gap-6 px-6 pb-6">
                      {paginatedCardData.map((prediction, index) => (
                        <PredictionCard key={`${cardCurrentPage}-${index}`} prediction={prediction} userRank={userInputs.rank} />
                      ))}
                      {paginatedCardData.length === 0 && filteredPredictions.length > 0 && (
                        <div className="text-center py-8 text-gray-500">
                          No results on this page. Try a different page or adjust filters.
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="w-full max-w-full overflow-x-auto">
                      <PredictionsTable 
                        predictions={paginatedData} 
                        userRank={userInputs.rank} 
                        formatCurrency={formatCurrency}
                        asTableRows={true}
                      />
                    </div>
                  )}
                </div>
                
                {/* Pagination - Fixed at bottom of container */}
                {viewMode === 'table' && (
                  <div className="bg-white border-t border-gray-200 px-6 py-3 flex items-center justify-between">
                    <div className="flex items-center text-sm text-gray-700">
                      <span>Rows: {rowsPerPage}</span>
                      <span className="ml-4">Page: {currentPage}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                                           <button
                       onClick={() => setCurrentPageNum(1)}
                       disabled={currentPage === 1}
                       className="px-2 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                     >
                       «
                     </button>
                     <button
                       onClick={() => setCurrentPageNum(prev => Math.max(prev - 1, 1))}
                       disabled={currentPage === 1}
                       className="px-2 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                     >
                       ‹
                     </button>
                     <span className="px-3 py-1 bg-blue-600 text-white rounded text-sm">
                       {currentPage}
                     </span>
                     {currentPage < totalPages - 1 && (
                       <button
                         onClick={() => setCurrentPageNum(currentPage + 1)}
                         className="px-2 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50"
                       >
                         {currentPage + 1}
                       </button>
                     )}
                     {currentPage < totalPages - 2 && (
                       <span className="px-2 py-1 text-sm text-gray-500">...</span>
                     )}
                     {totalPages > 1 && (
                       <button
                         onClick={() => setCurrentPageNum(totalPages)}
                         className="px-2 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50"
                       >
                         {totalPages}
                       </button>
                     )}
                     <button
                       onClick={() => setCurrentPageNum(prev => Math.min(prev + 1, totalPages))}
                       disabled={currentPage === totalPages}
                       className="px-2 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                     >
                       ›
                     </button>
                     <button
                       onClick={() => setCurrentPageNum(totalPages)}
                       disabled={currentPage === totalPages}
                       className="px-2 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                     >
                       »
                     </button>
                    </div>
                  </div>
                )}

                {/* Card view pagination */}
                {viewMode === 'cards' && cardTotalPages > 1 && (
                  <div className="bg-white border-t border-gray-200 px-6 py-3 flex items-center justify-between">
                    <div className="flex items-center text-sm text-gray-700">
                      <span>Cards: {cardRowsPerPage} per page</span>
                      <span className="ml-4">Page: {cardCurrentPage} of {cardTotalPages}</span>
                      <span className="ml-4">Showing: {((cardCurrentPage - 1) * cardRowsPerPage) + 1}-{Math.min(cardCurrentPage * cardRowsPerPage, filteredPredictions.length)} of {filteredPredictions.length}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                       onClick={() => setCardCurrentPage(1)}
                       disabled={cardCurrentPage === 1}
                       className="px-2 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                     >
                       «
                     </button>
                     <button
                       onClick={() => setCardCurrentPage(prev => Math.max(prev - 1, 1))}
                       disabled={cardCurrentPage === 1}
                       className="px-2 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                     >
                       ‹
                     </button>
                     <span className="px-3 py-1 bg-blue-600 text-white rounded text-sm">
                       {cardCurrentPage}
                     </span>
                     {cardCurrentPage < cardTotalPages - 1 && (
                       <button
                         onClick={() => setCardCurrentPage(cardCurrentPage + 1)}
                         className="px-2 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50"
                       >
                         {cardCurrentPage + 1}
                       </button>
                     )}
                     {cardCurrentPage < cardTotalPages - 2 && (
                       <span className="px-2 py-1 text-sm text-gray-500">...</span>
                     )}
                     {cardTotalPages > 1 && (
                       <button
                         onClick={() => setCardCurrentPage(cardTotalPages)}
                         className="px-2 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50"
                       >
                         {cardTotalPages}
                       </button>
                     )}
                     <button
                       onClick={() => setCardCurrentPage(prev => Math.min(prev + 1, cardTotalPages))}
                       disabled={cardCurrentPage === cardTotalPages}
                       className="px-2 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                     >
                       ›
                     </button>
                     <button
                       onClick={() => setCardCurrentPage(cardTotalPages)}
                       disabled={cardCurrentPage === cardTotalPages}
                       className="px-2 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                     >
                       »
                     </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {filteredPredictions.length === 0 && predictions.length > 0 && (
              <div className="text-center py-12">
                <div className="bg-gray-100 rounded-full p-4 w-16 h-16 mx-auto mb-4">
                  <Filter className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No results match your filters</h3>
                <p className="text-gray-600 mb-4">Try adjusting your filter criteria to see more results.</p>
                <button
                  onClick={resetFilters}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Reset Filters
                </button>
              </div>
            )}

            {predictions.length === 0 && userInputs.rank && !loading && (
              <div className="text-center py-12">
                <div className="bg-gray-100 rounded-full p-4 w-16 h-16 mx-auto mb-4">
                  <Search className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No predictions found</h3>
                <p className="text-gray-600">Try adjusting your criteria to see more results.</p>
              </div>
            )}


          </div>
        </div>
      </div>
    </div>
  );
};

const NEETPGPredictor = () => {
  const [currentPage, setCurrentPage] = useState('input');
  const [filteredPredictions, setFilteredPredictions] = useState([]);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
  
  const [userInputs, setUserInputs] = useState({
    rank: '',
    quota: '',
    category: '',
    preferredCourses: [],
    budget: ''
  });
  
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [filteringLoading, setFilteringLoading] = useState(false);
  const [currentPageNum, setCurrentPageNum] = useState(1);
  const [rowsPerPage] = useState(25);
  
  // Card view pagination state
  const [cardCurrentPage, setCardCurrentPage] = useState(1);
  const [cardRowsPerPage] = useState(20); // 20 cards per page for better UX
  
  const [filters, setFilters] = useState({
    colleges: [],
    courses: [],
    states: [],
    ownershipTypes: [],
    quotas: [],
    categories: [],
    feeRange: { min: 0, max: 0 },
    stipendRange: { min: 0, max: 0 },
    clinicalTypes: [],
    bondRequired: 'all',
    chanceLevel: 'all'
  });

  // Separate search state to prevent unnecessary re-renders
  const [searchTerms, setSearchTerms] = useState({
    collegesSearch: '',
    coursesSearch: '',
    statesSearch: '',
    ownershipTypesSearch: '',
    clinicalTypesSearch: ''
  });

  const [csvData, setCsvData] = useState({
    colleges: [],
    courses: [],
    fees: [],
    allotments: []
  });

  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState(null);

  const filterOptionsRef = useRef({});

  // Table pagination logic
  const totalRows = filteredPredictions.length;
  const totalPages = Math.ceil(totalRows / rowsPerPage);
  const startIndex = (currentPageNum - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedData = filteredPredictions.slice(startIndex, endIndex);

  // Card pagination logic
  const cardTotalPages = Math.ceil(totalRows / cardRowsPerPage);
  const cardStartIndex = (cardCurrentPage - 1) * cardRowsPerPage;
  const cardEndIndex = cardStartIndex + cardRowsPerPage;
  const paginatedCardData = filteredPredictions.slice(cardStartIndex, cardEndIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPageNum(1);
    setCardCurrentPage(1);
  }, [filteredPredictions.length]);

  // Load data from backend on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setDataLoading(true);
        setDataError(null);
        
        console.log('Loading data from backend...');
        const data = await apiService.fetchAllData();
        
        setCsvData(data);
      setDataLoaded(true);
        setDataLoading(false);
        
        console.log('Data loaded successfully:', {
          colleges: data.colleges.length,
          courses: data.courses.length,
          fees: data.fees.length,
          allotments: data.allotments.length
        });
      } catch (error) {
        console.error('Failed to load data:', error);
        setDataError(error.message);
        setDataLoading(false);
        setDataLoaded(false);
      }
    };

    loadData();
  }, []);

  const getUniqueValues = (data, key) => {
    return [...new Set(data.map(item => item[key]))].filter(Boolean).sort();
  };

  // Memoize data lengths to stabilize dependencies
  const dataLengths = useMemo(() => ({
    colleges: csvData.colleges?.length || 0,
    courses: csvData.courses?.length || 0,
    allotments: csvData.allotments?.length || 0
  }), [csvData.colleges?.length, csvData.courses?.length, csvData.allotments?.length]);

  const filterOptions = useMemo(() => {
    if (!dataLoaded || dataLengths.allotments === 0) return filterOptionsRef.current;
    
    const options = {
      colleges: getUniqueValues(csvData.colleges, 'college_name'),
      courses: getUniqueValues(csvData.courses, 'course_name'),
      states: getUniqueValues(csvData.colleges, 'state'),
      ownershipTypes: getUniqueValues(csvData.colleges, 'management_type'),
      quotas: getUniqueValues(csvData.allotments, 'quota'),
      categories: getUniqueValues(csvData.allotments, 'allotted_category'),
      clinicalTypes: getUniqueValues(csvData.courses, 'clinical_type')
    };
    
    filterOptionsRef.current = options;
    return options;
  }, [dataLoaded, dataLengths.allotments, csvData.colleges, csvData.courses, csvData.allotments]);

  // Find the latest year in allotments - optimized for large datasets
  const latestYear = useMemo(() => {
    if (!csvData.allotments || csvData.allotments.length === 0) return null;
    
    // For large datasets, use more efficient approach
    if (csvData.allotments.length > 10000) {
      let maxYear = 0;
      for (let i = 0; i < csvData.allotments.length; i++) {
        const year = Number(csvData.allotments[i].year) || 0;
        if (year > maxYear) maxYear = year;
      }
      return maxYear;
    }
    
    return Math.max(...csvData.allotments.map(a => Number(a.year) || 0));
  }, [csvData.allotments]);

  const handleInputChange = useCallback((field, value) => {
    setUserInputs(prev => {
      if (prev[field] === value) return prev;
      return {
        ...prev,
        [field]: value
      };
    });
  }, []);

  const generatePredictions = () => {
    if (!userInputs.rank || !userInputs.quota || !userInputs.category || !dataLoaded) return [];
    const userRank = parseInt(userInputs.rank);
    
    // Check if we're using optimized data (combined table) or individual tables
    const isOptimizedData = csvData.allotments && csvData.allotments.length > 0 && csvData.allotments[0].hasOwnProperty('closing_rank');
    
    if (isOptimizedData) {
      // Using optimized combined table data
      console.log('Using optimized combined table data for predictions');
      
      // FIRST: Group all allotments by college-course-quota-category combinations
      const groupedAllotments = {};
      csvData.allotments.forEach(allotment => {
        const key = `${allotment.college_name}-${allotment.course_name}-${allotment.quota}-${allotment.allotted_category}`;
        if (!groupedAllotments[key]) {
          groupedAllotments[key] = [];
        }
        groupedAllotments[key].push(allotment);
      });

      // SECOND: Filter groups based on the WORST (highest) closing rank for comparison
      const filteredGroups = Object.entries(groupedAllotments).filter(([key, allotmentGroup]) => {
        // Find the WORST (highest) closing rank for this college-course-quota-category combination
        const worstClosingRank = Math.max(...allotmentGroup.map(a => a.closing_rank || a.rank));
        
        // User should be able to get this seat if their rank is better than or equal to the WORST closing rank
        let rankMatches = userRank <= worstClosingRank;
        
        // If no direct match, check with tolerance against the worst rank
        if (!rankMatches) {
          const tolerance = worstClosingRank * 0.1;
          rankMatches = userRank <= (worstClosingRank + tolerance);
        }
        
        if (!rankMatches) return false;
        
        // Check quota filter
        if (userInputs.quota && allotmentGroup[0].quota !== userInputs.quota) {
          return false;
        }
        
        // Check category filter
        if (userInputs.category && allotmentGroup[0].allotted_category !== userInputs.category) {
          return false;
        }
        
        // Check preferred courses filter
        if (userInputs.preferredCourses.length > 0 && !userInputs.preferredCourses.includes(allotmentGroup[0].course_name)) {
          return false;
        }
        
        return true;
      });

      // THIRD: Create predictions with ALL historical data for matching combinations
      let predictions = filteredGroups.map(([key, allotmentGroup]) => {
        // Find the latest year
        const latestYear = Math.max(...allotmentGroup.map(a => a.year));
        
        // Find records from the latest year
        const latestYearRecords = allotmentGroup.filter(a => a.year === latestYear);
        
        // Find the record with the LATEST ROUND (highest round number) for the latest year
        const latestRoundRecord = latestYearRecords.reduce((latest, current) => {
          const currentRound = current.round || 0;
          const latestRound = latest.round || 0;
          return currentRound > latestRound ? current : latest;
        });

        // Use the closing rank of the latest round in the latest year for chance calculation
        const closingRankForChance = latestRoundRecord.closing_rank || latestRoundRecord.rank;

        let chanceLevel, chanceText, chanceColor;
        if (userRank <= closingRankForChance) {
          chanceLevel = 'high';
          chanceText = 'High Chance';
          chanceColor = 'green';
        } else if (userRank <= closingRankForChance + closingRankForChance * 0.1) {
          chanceLevel = 'moderate';
          chanceText = 'Moderate Chance';
          chanceColor = 'yellow';
        } else {
          chanceLevel = 'low';
          chanceText = 'Low Chance';
          chanceColor = 'gray';
        }

        // For optimized data, the college and course info is already included
        const college = {
          college_name: latestRoundRecord.college_name,
          state: latestRoundRecord.state,
          established_year: latestRoundRecord.established_year,
          no_of_beds: latestRoundRecord.no_of_beds,
          management_type: latestRoundRecord.management_type
        };
        const course = {
          course_name: latestRoundRecord.course_name,
          clinical_type: latestRoundRecord.clinical_type,
          degree_type: latestRoundRecord.degree_type,
          duration: latestRoundRecord.duration
        };
        const fee = {
          course_fee: latestRoundRecord.course_fee,
          stipend_y1: latestRoundRecord.stipend_y1,
          stipend_y2: latestRoundRecord.stipend_y2,
          stipend_y3: latestRoundRecord.stipend_y3,
          bond: latestRoundRecord.bond,
          bond_penalty: latestRoundRecord.bond_penalty
        };
        const historicalData = allotmentGroup.map(a => ({
          year: a.year,
          round: a.round,
          rank: a.closing_rank || a.rank,
          quota: a.quota,
          allotted_category: a.allotted_category
        }));

        return {
          ...latestRoundRecord, // Use the latest round record for the comparison display
          college,
          course,
          fee,
          chanceLevel,
          chanceText,
          chanceColor,
          userRank,
          closingRank: closingRankForChance, // Use latest round closing rank for display
          historicalData
        };
      });

      if (userInputs.budget) {
        const budgetLimit = parseInt(userInputs.budget);
        predictions = predictions.filter(p => p.fee?.course_fee <= budgetLimit);
      }

      predictions.sort((a, b) => {
        const chanceOrder = { high: 0, moderate: 1, low: 2 };
        if (chanceOrder[a.chanceLevel] !== chanceOrder[b.chanceLevel]) {
          return chanceOrder[a.chanceLevel] - chanceOrder[b.chanceLevel];
        }
        return a.closingRank - b.closingRank;
      });

      return predictions;
    } else {
      // Original logic for individual tables (fallback)
      console.log('Using individual tables data for predictions');
      
      // Create lookup maps for better performance with large datasets
      const collegeMap = new Map();
      csvData.colleges.forEach(college => {
        collegeMap.set(college.college_name, college);
      });
      
      const courseMap = new Map();
      csvData.courses.forEach(course => {
        courseMap.set(course.course_name, course);
      });
      
      const feeMap = new Map();
      csvData.fees.forEach(fee => {
        const key = `${fee.college_name}-${fee.course_name}-${fee.quota}`;
        feeMap.set(key, fee);
      });
      
      // Group allotments by key for historical data lookup
      const historicalMap = new Map();
      csvData.allotments.forEach(allotment => {
        const key = `${allotment.college_name}-${allotment.course_name}-${allotment.quota}-${allotment.allotted_category}`;
        if (!historicalMap.has(key)) {
          historicalMap.set(key, []);
        }
        historicalMap.get(key).push({
          year: allotment.year,
          round: allotment.round,
          rank: allotment.rank
        });
      });
      
      // Find latest year allotments and group by unique combinations
    const groupedData = {};
    csvData.allotments.forEach(allotment => {
      if (allotment.year === latestYear) {
        const key = `${allotment.college_name}-${allotment.course_name}-${allotment.quota}-${allotment.allotted_category}`;
        if (!groupedData[key] || allotment.rank > groupedData[key].rank) {
          groupedData[key] = allotment;
        }
      }
    });
    const uniqueAllotments = Object.values(groupedData);
    
    const filteredAllotments = uniqueAllotments.filter(allotment => {
        // Check if user rank is better than or equal to the WORST closing rank for this college-course-quota-category combination
        const historicalKey = `${allotment.college_name}-${allotment.course_name}-${allotment.quota}-${allotment.allotted_category}`;
        const historicalData = historicalMap.get(historicalKey) || [];
        
        // Find the WORST (highest) closing rank for this combination
        const worstClosingRank = historicalData.length > 0 ? Math.max(...historicalData.map(h => h.rank)) : allotment.rank;
        
        let rankMatches = userRank <= worstClosingRank;
        
        // If no direct match, check with tolerance against the worst rank
        if (!rankMatches) {
          const tolerance = worstClosingRank * 0.1;
          rankMatches = userRank <= (worstClosingRank + tolerance);
      }
      
      if (!rankMatches) return false;
      
      if (userInputs.quota && allotment.quota !== userInputs.quota) {
        return false;
      }
      
      if (userInputs.category && allotment.allotted_category !== userInputs.category) {
        return false;
      }
      
      if (userInputs.preferredCourses.length > 0 && !userInputs.preferredCourses.includes(allotment.course_name)) {
        return false;
      }
      
      return true;
    });

    let predictions = filteredAllotments.map(allotment => {
        const college = collegeMap.get(allotment.college_name) || {};
        const course = courseMap.get(allotment.course_name) || {};
        const feeKey = `${allotment.college_name}-${allotment.course_name}-${allotment.quota}`;
        const fee = feeMap.get(feeKey) || {};

        // Get historical data using the lookup map
        const historicalKey = `${allotment.college_name}-${allotment.course_name}-${allotment.quota}-${allotment.allotted_category}`;
        const historicalData = historicalMap.get(historicalKey) || [];

        // Find the latest year
        const latestYear = Math.max(...historicalData.map(h => h.year));
        
        // Find records from the latest year
        const latestYearRecords = historicalData.filter(h => h.year === latestYear);
        
        // Find the record with the LATEST ROUND (highest round number) for the latest year
        const latestRoundRecord = latestYearRecords.reduce((latest, current) => {
          const currentRound = current.round || 0;
          const latestRound = latest.round || 0;
          return currentRound > latestRound ? current : latest;
        });

        // Use the WORST (highest) closing rank for chance calculation
        const worstClosingRank = historicalData.length > 0 ? Math.max(...historicalData.map(h => h.rank)) : allotment.rank;

        let chanceLevel, chanceText, chanceColor;
        
        if (userRank <= worstClosingRank) {
          chanceLevel = 'high';
          chanceText = 'High Chance';
          chanceColor = 'green';
        } else {
          chanceLevel = 'moderate';
          chanceText = 'Moderate Chance';
          chanceColor = 'yellow';
        }

        return {
          ...allotment,
          college,
          course,
          fee,
          chanceLevel,
          chanceText,
          chanceColor,
          userRank,
          closingRank: latestRoundRecord ? latestRoundRecord.rank : allotment.rank, // Use latest round closing rank for display
          historicalData: historicalData.map(h => ({
            year: h.year,
            round: h.round,
            rank: h.rank,
            quota: allotment.quota,
            allotted_category: allotment.allotted_category
          }))
        };
    });

    if (userInputs.budget) {
      const budgetLimit = parseInt(userInputs.budget);
      predictions = predictions.filter(p => p.fee?.course_fee <= budgetLimit);
    }

    predictions.sort((a, b) => {
      if (a.chanceLevel !== b.chanceLevel) {
        return a.chanceLevel === 'high' ? -1 : 1;
      }
      return a.rank - b.rank;
    });

    return predictions;
    }
  };

  const handlePredict = () => {
    if (!userInputs.rank || !userInputs.quota || !userInputs.category || !dataLoaded) return;

    setLoading(true);
    setTimeout(() => {
      const results = generatePredictions();
      setPredictions(results);
      setFilteredPredictions(results);
      setLoading(false);
      setCurrentPage('results');
    }, 1000);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const applyFilters = useCallback(async () => {
    if (predictions.length === 0) return;
    
    setFilteringLoading(true);
    
    // Use setTimeout to allow UI to update before heavy computation
    setTimeout(() => {
      let filtered = [...predictions];

      // Optimize by creating Set lookups for better performance with large datasets
      const collegeSet = new Set(filters.colleges);
      const courseSet = new Set(filters.courses);
      const stateSet = new Set(filters.states);
      const ownershipSet = new Set(filters.ownershipTypes);
      const quotaSet = new Set(filters.quotas);
      const categorySet = new Set(filters.categories);
      const clinicalSet = new Set(filters.clinicalTypes);

    filtered = filtered.filter(p => {
        // Apply all filters in a single pass for better performance
        if (collegeSet.size > 0 && !collegeSet.has(p.college.college_name)) return false;
        if (courseSet.size > 0 && !courseSet.has(p.course.course_name)) return false;
        if (stateSet.size > 0 && !stateSet.has(p.college.state)) return false;
        if (ownershipSet.size > 0 && !ownershipSet.has(p.college.management_type)) return false;
        if (quotaSet.size > 0 && !quotaSet.has(p.quota)) return false;
        if (categorySet.size > 0 && !categorySet.has(p.allotted_category)) return false;
        if (clinicalSet.size > 0 && !clinicalSet.has(p.course.clinical_type)) return false;

      const fee = p.fee?.course_fee || 0;
        if (fee < filters.feeRange.min || fee > filters.feeRange.max) return false;

      const stipend = p.fee?.stipend_y1 || 0;
        if (stipend < filters.stipendRange.min || stipend > filters.stipendRange.max) return false;

    if (filters.bondRequired !== 'all') {
        const bondYears = Number(p.fee?.bond) || 0;
          if (filters.bondRequired === 'yes' ? bondYears <= 0 : bondYears > 0) return false;
        }

        if (filters.chanceLevel !== 'all' && p.chanceLevel !== filters.chanceLevel) return false;

        return true;
      });

    setFilteredPredictions(filtered);
      setFilteringLoading(false);
    }, 10); // Small delay to allow UI update
  }, [predictions, filters]);

  const resetFilters = () => {
    setFilters({
      colleges: [],
      courses: [],
      states: [],
      ownershipTypes: [],
      quotas: [],
      categories: [],
      feeRange: { min: 0, max: maxFee },
      stipendRange: { min: 0, max: maxStipend },
      clinicalTypes: [],
      bondRequired: 'all',
      chanceLevel: 'all'
    });
    setSearchTerms({
      collegesSearch: '',
      coursesSearch: '',
      statesSearch: '',
      ownershipTypesSearch: '',
      clinicalTypesSearch: ''
    });
    setFilteredPredictions(predictions);
  };

  const updateFilter = (filterType, value) => {
    setFilters(prev => {
      let newFilters = { ...prev };
      
      if (filterType === 'feeRange' || filterType === 'stipendRange') {
        newFilters[filterType] = value;
      } else if (filterType === 'bondRequired' || filterType === 'chanceLevel') {
        newFilters[filterType] = value;
      } else {
        // Ensure the array exists before calling includes
        if (!newFilters[filterType]) {
          newFilters[filterType] = [];
        }
        if (newFilters[filterType].includes(value)) {
          newFilters[filterType] = newFilters[filterType].filter(item => item !== value);
        } else {
          newFilters[filterType] = [...newFilters[filterType], value];
        }
      }
      
      return newFilters;
    });
  };

  useEffect(() => {
    if (predictions.length > 0) {
      applyFilters();
    }
  }, [applyFilters, predictions.length]);

  // Retry function for failed data loading
  const retryDataLoad = async () => {
    try {
      setDataLoading(true);
      setDataError(null);
      
      console.log('Retrying data load...');
      const data = await apiService.fetchAllData();
      
    setCsvData(data);
      setDataLoaded(true);
      setDataLoading(false);
      
      console.log('Data loaded successfully on retry');
    } catch (error) {
      console.error('Retry failed:', error);
      setDataError(error.message);
      setDataLoading(false);
      setDataLoaded(false);
    }
  };

  // Dynamically determine max fee from uploaded data
  const maxFee = useMemo(() => {
    if (!csvData.fees || csvData.fees.length === 0) return 2000000;
    return Math.max(
      ...csvData.fees
        .map(f => {
          const cleaned = String(f.course_fee).replace(/[^\d.]/g, '');
          return cleaned ? parseFloat(cleaned) : 0;
        })
        .filter(n => !isNaN(n) && n > 0),
      2000000
    );
  }, [csvData.fees]);

  // Dynamically determine max stipend (stipend_y1) from uploaded data
  const maxStipend = useMemo(() => {
    if (!csvData.fees || csvData.fees.length === 0) return 100000;
    return Math.max(
      ...csvData.fees
        .map(f => {
          const cleaned = String(f.stipend_y1).replace(/[^\d.]/g, '');
          return cleaned ? parseFloat(cleaned) : 0;
        })
        .filter(n => !isNaN(n) && n > 0),
      100000
    );
  }, [csvData.fees]);

  // Update fee and stipend range filters to match max values after data is loaded
  useEffect(() => {
    if (dataLoaded) {
      setFilters(prev => ({
        ...prev,
        feeRange: { min: 0, max: maxFee },
        stipendRange: { min: 0, max: maxStipend }
      }));
    }
  }, [dataLoaded, maxFee, maxStipend]);

  // Show loading screen while data is being fetched
  if (dataLoading || dataError) {
    return <DataLoader loading={dataLoading} error={dataError} onRetry={retryDataLoad} />;
  }

  return (
    <div>
      {currentPage === 'input' ? (
        <InputPage
          userInputs={userInputs}
          handleInputChange={handleInputChange}
          handlePredict={handlePredict}
          loading={loading}
          filterOptions={filterOptions}
          maxFee={maxFee}
          maxStipend={maxStipend}
        />
      ) : (
        <ResultsPage
          userInputs={userInputs}
          predictions={predictions}
          filteredPredictions={filteredPredictions}
          filters={filters}
          filterOptions={filterOptions}
          formatCurrency={formatCurrency}
          resetFilters={resetFilters}
          updateFilter={updateFilter}
          loading={loading}
          setCurrentPage={setCurrentPage}
          maxFee={maxFee}
          maxStipend={maxStipend}
          viewMode={viewMode}
          setViewMode={setViewMode}
          currentPage={currentPageNum}
          rowsPerPage={rowsPerPage}
          paginatedData={paginatedData}
          totalPages={totalPages}
          setCurrentPageNum={setCurrentPageNum}
          cardCurrentPage={cardCurrentPage}
          setCardCurrentPage={setCardCurrentPage}
          cardTotalPages={cardTotalPages}
          paginatedCardData={paginatedCardData}
          cardRowsPerPage={cardRowsPerPage}
          filteringLoading={filteringLoading}
          searchTerms={searchTerms}
          setSearchTerms={setSearchTerms}
        />
      )}
    </div>
  );
};

export default NEETPGPredictor;
